import { MediaItem } from '../../../api/structs/media';
import { readContentVersion } from '../../../api/mediaUrls';
import { isNewerFailure, readRegenerateFailure } from '../regenerateFailure';

/**
 * IN-FLIGHT state for a regeneration, held ABOVE the modal that starts it.
 *
 * `POST /media/:id/regenerate` answers `200 {}` immediately and does the work
 * in a goroutine — no job id, nothing to poll. The storyboard's only spinner is
 * driven off the CANONICAL item sitting on the `_waiting` placeholder, and a
 * non-destructive regenerate deliberately never puts it there (the previous
 * take must keep playing for the whole provider call). So between "Regenerate"
 * and the result landing there was no visible state at all: the modal closed,
 * one green toast, and a storyboard that re-rendered byte-identical.
 *
 * This is that missing state. It lives in `MediaEditorSection` — NOT in the
 * modal, which unmounts on submit — keyed by media id, and it ends when the
 * item actually changes or a failure lands on it.
 */
export interface RegenerateWatch {
  /** Epoch ms the POST succeeded — the watchdog's clock. */
  startedAt: number;
  /** The item's file_url when the attempt started; a move means it landed. */
  fileUrl: string;
  /** Belt-and-braces for an in-place byte rewrite that reuses the URL. */
  contentVersion?: number;
  /** `last_regenerate_failed_at` at start; a NEWER stamp means THIS attempt failed. */
  failedAt: number;
  /** Set by the watchdog: still waiting, long past any plausible generation. */
  stalled?: boolean;
}

export type RegenerateWatchMap = Record<string, RegenerateWatch>;

export type RegenerateStatus = 'running' | 'stalled';

/**
 * How long "Regenerating…" may stand with nothing arriving before it turns into
 * "no result arrived".
 *
 * Deliberately the backend's own `regenerateLeaseTTL` (30 min,
 * internal/api/media/regenerateLease.go). Until that lease expires the server
 * still considers a regeneration of this item in flight and answers a second
 * attempt with 409 — so giving up EARLIER would tell the user "nothing is
 * running, try again" while the only thing they could do about it is fail. The
 * bound exists so the state cannot spin forever unexplained, not to guess when
 * a provider is late: a real failure lands via `last_regenerate_failed_at` long
 * before this, and a real success moves file_url.
 */
export const REGENERATE_WATCHDOG_MS = 30 * 60 * 1000;

/** Snapshots `item` as the thing to wait for. Replaces any earlier watch. */
export const beginRegenerateWatch = (
  watches: RegenerateWatchMap,
  item: MediaItem,
  now: number = Date.now()
): RegenerateWatchMap => ({
  ...watches,
  [item.id]: {
    startedAt: now,
    fileUrl: item.file_url,
    contentVersion: readContentVersion(item.metadata),
    failedAt: readRegenerateFailure(item)?.failedAt ?? 0,
  },
});

/**
 * Drops every watch whose item has settled — new bytes, a new content version,
 * a newer failure stamp, or the item disappearing entirely (deleted, or swapped
 * out from under the slot). Returns the SAME map object when nothing changed so
 * callers can pass it straight to `setState` without re-rendering.
 */
export const settleRegenerateWatches = (
  watches: RegenerateWatchMap,
  items: MediaItem[]
): RegenerateWatchMap => {
  const ids = Object.keys(watches);
  if (ids.length === 0) return watches;

  const next: RegenerateWatchMap = {};
  let changed = false;
  ids.forEach((id) => {
    const watch = watches[id];
    const item = items.find((entry) => entry.id === id);
    if (!item) {
      changed = true;
      return;
    }
    const settled =
      item.file_url !== watch.fileUrl ||
      readContentVersion(item.metadata) !== watch.contentVersion ||
      isNewerFailure(readRegenerateFailure(item), watch.failedAt);
    if (settled) {
      changed = true;
      return;
    }
    next[id] = watch;
  });
  return changed ? next : watches;
};

/**
 * Flags watches past the watchdog bound. They are KEPT rather than dropped: the
 * flag is the explanation the user is owed, and losing it would put the scene
 * back to looking exactly as it did before they pressed anything.
 */
export const expireRegenerateWatches = (
  watches: RegenerateWatchMap,
  now: number = Date.now()
): RegenerateWatchMap => {
  let changed = false;
  const next: RegenerateWatchMap = {};
  Object.entries(watches).forEach(([id, watch]) => {
    if (!watch.stalled && now - watch.startedAt >= REGENERATE_WATCHDOG_MS) {
      changed = true;
      next[id] = { ...watch, stalled: true };
      return;
    }
    next[id] = watch;
  });
  return changed ? next : watches;
};

/** The status to render for one media id, or null when nothing is in flight. */
export const regenerateStatusOf = (
  watches: RegenerateWatchMap,
  mediaId: string | undefined
): RegenerateStatus | null => {
  if (!mediaId) return null;
  const watch = watches[mediaId];
  if (!watch) return null;
  return watch.stalled ? 'stalled' : 'running';
};

/** The status for a whole scene: any item of it being regenerated counts. */
export const sceneRegenerateStatus = (
  watches: RegenerateWatchMap,
  mediaIds: (string | undefined)[]
): RegenerateStatus | null => {
  let status: RegenerateStatus | null = null;
  mediaIds.forEach((id) => {
    const item = regenerateStatusOf(watches, id);
    // 'running' wins over 'stalled': something in this scene is still live.
    if (item === 'running') status = 'running';
    else if (item === 'stalled' && status === null) status = 'stalled';
  });
  return status;
};
